// menu.jsx - Updated imports and functions
import { useState, useEffect, useRef } from "react";
import { connectPrinter } from "../../../Shared/Hardware/ConnectPrinter";
import { closeCashJournal } from "../../../Services/JournalApi";
import { performLogout } from "../../../Auth/useToken";
import {
  Modal,
  Box,
  Typography,
  TextField,
  Button,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Alert,
} from "@mui/material";
import { toast } from "react-toastify";
import { ImpressionTicket } from "../../../Helpers/Helpers";
import { PrintAction } from "../../../Shared/Hardware/PrintActions";
import iconv from "iconv-lite";
import { getMenusreadyToServe } from "../../../Services/MenuApi";
import {
  getCaissesByOrganisation,
  transferStockBetweenCaisses,
  getCaisseStock,
  getTransferHistory, // NEW import
  getMovementsByType, // NEW import
} from "../../../Services/CaisseApi";
import { useNavigate } from "react-router-dom";
import { useOrganisations } from "../../../Auth/useOrganisations";
import { useUser } from "../../../Auth/useUser";
import KeyboardComponent from "../keyboards/virtualKeypad";

const GlobalMenu = ({
  onOrdersClick,
  onLogoutClick,
  onTablesClick,
  parametrages,
}) => {
  const navigate = useNavigate();
  const user = useUser();
  const isAdmin = user?.result?.role === "admin";
  const [
    organisations,
    setOrganisations,
    usedOrganisations,
    setUsedOrganisations,
  ] = useOrganisations();
  const [showCloseJournalModal, setShowCloseJournalModal] = useState(false);
  const [showTransferStockModal, setShowTransferStockModal] = useState(false);
  const [fondFinal, setFondFinal] = useState("");
  // true until the keypad's first keystroke — lets that keystroke replace
  // the pre-filled default instead of appending to it
  const [fondFinalFresh, setFondFinalFresh] = useState(true);
  const [activeJournal, setActiveJournal] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [currentFund, setCurrentFund] = useState(0);
  const [menuExpanded, setMenuExpanded] = useState(false);
  const leftMenuRef = useRef(null);
  const [menuHeight, setMenuHeight] = useState(80);
  const storedCaisse = JSON.parse(localStorage.getItem("caisse_data"));
  const storedCaisseID = storedCaisse ? storedCaisse.id : null;
  // Transfer Stock States
  const [caisses, setCaisses] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedFromCaisse, setSelectedFromCaisse] = useState(null);
  const [selectedToCaisse, setSelectedToCaisse] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [transferQuantity, setTransferQuantity] = useState("");
  const [availableStock, setAvailableStock] = useState(0);
  const [transferHistory, setTransferHistory] = useState([]);
  const [loadingCaisses, setLoadingCaisses] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);

  useEffect(() => {
    if (leftMenuRef.current) {
      const height = leftMenuRef.current.scrollHeight;
      const newHeight = Math.min(height + 24, 160);
      setMenuHeight(newHeight);
    }
  }, []);

  useEffect(() => {
    console.log(parametrages);
    const loadActiveJournal = () => {
      try {
        const storedJournal = localStorage.getItem("activeJournal");
        if (storedJournal) {
          const journal = JSON.parse(storedJournal);
          setActiveJournal(journal);
          setCurrentFund(journal.fond_courant.toFixed(3) || 0);
          setFondFinal(
            parametrages && parametrages.defaultFondClotureInput
              ? journal.fond_courant?.toFixed(3).toString()
              : "0",
          );
          setFondFinalFresh(true);
          // Set default from caisse to current caisse
          setSelectedFromCaisse(journal.caisse_id);
        }
      } catch (error) {
        console.error("Error loading active journal:", error);
      }
    };

    loadActiveJournal();
  }, [showCloseJournalModal]);

  // Load caisses when modal opens
  useEffect(() => {
    if (showTransferStockModal) {
      loadCaisses();
      loadProducts();
      loadTransferHistory();
    }
  }, [showTransferStockModal]);

  // Update available stock when product or from caisse changes
  useEffect(() => {
    if (selectedFromCaisse && selectedProduct) {
      fetchAvailableStock();
    }
  }, [selectedFromCaisse, selectedProduct]);

  const loadCaisses = async () => {
    try {
      setLoadingCaisses(true);
      const organisationId =
        activeJournal?.organisation_id ||
        localStorage.getItem("organisationId");

      const response = await getCaissesByOrganisation(organisationId);

      // Handle different response formats
      const result = response.data?.data || response.data;
      console.log("Caisses loaded:", result);

      if (Array.isArray(result)) {
        const currentCaisseId = storedCaisseID;
        // Filter out current caisse
        const filtered = result.filter(
          (c) => c.id?.toString() !== currentCaisseId,
        );
        setCaisses(filtered);
      }
    } catch (error) {
      console.error("Error loading caisses:", error);
      toast.error("Erreur chargement caisses");
    } finally {
      setLoadingCaisses(false);
    }
  };

  const loadProducts = async () => {
    try {
      setLoadingProducts(true);
      const organisationId =
        activeJournal?.organisation_id ||
        localStorage.getItem("organisationId");

      const response = await getMenusreadyToServe(organisationId);
      const result = response.data?.data || response.data;

      if (Array.isArray(result)) {
        // Filter for products that have stock management enabled

        setProducts(result);
      }
    } catch (error) {
      console.error("Error loading products:", error);
      toast.error("Erreur chargement produits");
    } finally {
      setLoadingProducts(false);
    }
  };

  const fetchAvailableStock = async () => {
    try {
      const response = await getCaisseStock(selectedFromCaisse);

      // Handle response format
      const stocks = response.data?.data || response.data;

      if (Array.isArray(stocks)) {
        const stockItem = stocks.find(
          (s) =>
            s.produit_id?._id === selectedProduct ||
            s.produit_id === selectedProduct ||
            s.product_id === selectedProduct,
        );
        const stockQty = stockItem?.quantite_stock || 0;
        setAvailableStock(stockQty);

        // Show toast if stock is low
        if (stockQty === 0) {
          toast.warning("Stock épuisé pour ce produit dans cette caisse");
        }
      }
    } catch (error) {
      console.error("Error fetching available stock:", error);
      setAvailableStock(0);
    }
  };

  const loadTransferHistory = async () => {
    try {
      setLoadingHistory(true);
      const organisationId =
        activeJournal?.organisation_id ||
        localStorage.getItem("organisationId");

      // Use the API function instead of direct axios
      const response = await getMovementsByType(
        organisationId,
        "transfert",
        10,
      );

      // Handle response format
      const movements = response.data?.data || response.data;

      if (Array.isArray(movements)) {
        // Process movements to show only 'sortie' entries (to avoid duplicates)
        const sortieMovements = movements.filter((m) => m.sens === "sortie");

        const formattedHistory = sortieMovements.map((movement) => ({
          id: movement._id || movement.id,
          date: movement.date_mouvement || movement.createdAt,
          from_caisse:
            movement.emplacement_nom ||
            movement.metadata?.from_caisse_name ||
            "Caisse source",
          to_caisse: movement.metadata?.to_caisse_name || "Caisse destination",
          product:
            movement.produit_id?.titre?.fr || movement.produit_nom || "Produit",
          quantity: movement.quantite,
          status: movement.status || "completed",
          user: movement.user_id?.nom || "Utilisateur",
          code: movement.code,
        }));

        setTransferHistory(formattedHistory);
      }
    } catch (error) {
      console.error("Error loading transfer history:", error);
      // Don't show error toast for history, just set empty
      setTransferHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleOpenCloseJournal = () => {
    if (!activeJournal) {
      toast.error("Aucun journal actif trouvé !");
      return;
    }

    if (activeJournal.cloture) {
      toast.error("Le journal est déjà clôturé !");
      return;
    }

    setFondFinal(activeJournal.fond_courant?.toString() || "0");
    setFondFinalFresh(true);
    setShowCloseJournalModal(true);
  };

  const handleOpenTransferStock = () => {
    if (!activeJournal) {
      toast.error("Aucun journal actif trouvé !");
      return;
    }

    if (activeJournal.cloture) {
      toast.error("Le journal est clôturé !");
      return;
    }

    setShowTransferStockModal(true);
  };

  const callImpression = async (dataTicket, type) => {
    try {
      console.log("Printing ticket:", type, dataTicket);
      const lines = ImpressionTicket(type, {
        ...dataTicket,
      });

      const finalBuffer = (() => {
        const allBytes = [];
        const buf = iconv.encode(lines, "ISO-8859-1");
        allBytes.push(...buf);
        return new Uint8Array(allBytes);
      })();

      await PrintAction(finalBuffer, true);
    } catch (error) {
      console.error("Print error:", error);
      // Don't throw, just log error
    }
  };

  const handleOpenDrawer = async () => {
    const ok = await PrintAction(null, true, false);
    if (!ok) {
      toast.error(
        "Impossible d'ouvrir le tiroir. Vérifiez la connexion de l'imprimante."
      );
    }
  };

  const handleCloseJournal = async () => {
    if (!fondFinal || isNaN(parseFloat(fondFinal))) {
      toast.error("Veuillez entrer un montant valide !");
      return;
    }

    const finalFundValue = parseFloat(fondFinal);

    if (finalFundValue < 0) {
      toast.error("Le fond final ne peut pas être négatif !");
      return;
    }

    setIsProcessing(true);

    try {
      const userData =
        localStorage.getItem("user") || sessionStorage.getItem("user");
      let validatorId = null;

      if (userData) {
        const user = JSON.parse(userData);
        validatorId = user.id || user._id;
      }

      const closeResponse = await closeCashJournal(
        activeJournal.id || activeJournal._id,
        {
          fond_final: finalFundValue,
          validator_id: validatorId,
          end_date: new Date(),
        },
      );

      if (closeResponse?.data?.success || closeResponse?.status === 200) {
        const closedJournal = closeResponse.data.data || closeResponse.data;
        await callImpression(closedJournal, "cloture");
        localStorage.setItem("activeJournal", JSON.stringify(closedJournal));
        setActiveJournal(closedJournal);

        toast.success(
          `Journal clôturé avec succès ! Fond final: ${finalFundValue.toFixed(
            3,
          )} TND`,
        );
        setShowCloseJournalModal(false);

        if (isAdmin) {
          // Admins stay logged in and are sent back to the dashboard —
          // only caissiers are forced to re-authenticate for a new journal.
          setTimeout(() => {
            navigate("/", { replace: true });
          }, 1500);
        } else {
          toast.info(
            "Veuillez vous reconnecter pour créer un nouveau journal",
          );

          // Log out and head straight to /login in one deterministic step —
          // giving performLogout()'s auth-change event time to propagate on
          // its own here left a window where a caissier could briefly see
          // a stale authenticated route before being bounced to /login.
          setTimeout(() => {
            performLogout({ preserveKeys: ["caisse_data"] });
            navigate("/login", { replace: true });
          }, 1500);
        }
      } else {
        toast.error("Erreur lors de la clôture du journal !");
      }
    } catch (error) {
      console.error("Error closing journal:", error);
      toast.error(
        error.response?.data?.message ||
          "Erreur lors de la clôture du journal !",
      );
    } finally {
      setIsProcessing(false);
    }
  };

  const handleTransferStock = async () => {
    if (!selectedToCaisse || !selectedProduct || !transferQuantity) {
      toast.error("Veuillez remplir tous les champs");
      return;
    }

    const quantity = parseInt(transferQuantity);
    if (quantity <= 0) {
      toast.error("La quantité doit être supérieure à 0");
      return;
    }

    setIsProcessing(true);

    try {
      const userData =
        localStorage.getItem("user") || sessionStorage.getItem("user");
      let userId = null;
      let userName = "Utilisateur";

      if (userData) {
        const user = JSON.parse(userData);
        userId = user.id || user._id;
        userName = user.nom || user.prenom || "Utilisateur";
      }

      // Get product details for better messaging
      const product = products.find((p) => p._id === selectedProduct);
      const productName = product?.titre?.fr || product?.titre || "Produit";

      // Get caisse names
      const fromCaisse = caisses.find((c) => c._id === selectedFromCaisse);
      const toCaisse = caisses.find((c) => c._id === selectedToCaisse);

      const transferData = {
        productId: selectedProduct,
        fromCaisseId: storedCaisseID,
        toCaisseId: selectedToCaisse,
        quantity: quantity,
        userId: userId,
        organisation_id:
          activeJournal?.organisation_id ||
          localStorage.getItem("organisationId"),
        notes: `Transfert manuel: ${productName} (${quantity}) de ${activeJournal?.caisse_nom || "caisse"} vers ${toCaisse?.nom || "destination"}`,
      };

      const response = await transferStockBetweenCaisses(transferData);

      // Check for success in different response formats
      if (response.data?.success || response.data?.data) {
        toast.success(
          `✅ Transfert de ${quantity} ${productName} effectué avec succès`,
        );

        // Print transfer receipt
        try {
          await callImpression(
            {
              from_caisse:
                activeJournal?.caisse_nom || fromCaisse?.nom || "Caisse source",
              to_caisse: toCaisse?.nom || "Caisse destination",
              product: productName,
              quantity: quantity,
              date: new Date().toLocaleString("fr-FR"),
              user: userName,
              code: response.data.data?.code || `TRSF-${Date.now()}`,
            },
            "transfert",
          );
        } catch (printError) {
          console.warn("Print error:", printError);
          // Don't block success if print fails
        }

        // Reset form
        setSelectedToCaisse(null);
        setSelectedProduct(null);
        setTransferQuantity("");
        setAvailableStock(0);

        // Refresh transfer history
        await loadTransferHistory();

        // Refresh available stock for current product
        if (selectedFromCaisse && selectedProduct) {
          await fetchAvailableStock();
        }

        // Close modal after short delay
        setTimeout(() => {
          setShowTransferStockModal(false);
        }, 2000);
      } else {
        toast.error(response.data?.errorMessage || "Erreur lors du transfert");
      }
    } catch (error) {
      console.error("Error transferring stock:", error);
      toast.error(
        error.response?.data?.message ||
          error.message ||
          "Erreur lors du transfert de stock",
      );
    } finally {
      setIsProcessing(false);
    }
  };

  // Close-journal modal virtual keyboard handler
  const handleFundKeyPress = (key) => {
    if (isProcessing) return;
    if (key === "←") {
      setFondFinal(fondFinalFresh ? "" : (v) => v.slice(0, -1));
      setFondFinalFresh(false);
    } else if (key === "prix" || key === "✔") {
      handleCloseJournal();
    } else if (key === ".") {
      if (fondFinalFresh) {
        setFondFinal("0.");
      } else if (!fondFinal.includes(".")) {
        setFondFinal((v) => v + ".");
      }
      setFondFinalFresh(false);
    } else if (/^\d$/.test(key)) {
      setFondFinal(fondFinalFresh ? key : (v) => v + key);
      setFondFinalFresh(false);
    }
    // ignore %, -
  };

  const handleModalClose = () => {
    if (!isProcessing) {
      setShowCloseJournalModal(false);
      setShowTransferStockModal(false);
      // Reset transfer form
      setSelectedToCaisse(null);
      setSelectedProduct(null);
      setTransferQuantity("");
      setAvailableStock(0);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("fr-FR", {
      minimumFractionDigits: 3,
      maximumFractionDigits: 3,
    }).format(amount || 0);
  };

  const toggleMenuExpansion = () => {
    setMenuExpanded(!menuExpanded);
  };

  // Select menu props for better dropdown positioning
  const selectMenuProps = {
    PaperProps: {
      style: {
        maxHeight: 300,
        zIndex: 9999,
      },
    },
    anchorOrigin: {
      vertical: "bottom",
      horizontal: "left",
    },
    transformOrigin: {
      vertical: "top",
      horizontal: "left",
    },
  };

  // Rest of your JSX remains the same...
  // (I'll keep your existing JSX as is since it's correct)

  return (
    <>
      {/* Close Journal Modal */}
      <Modal
        open={showCloseJournalModal}
        onClose={handleModalClose}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            width: 520,
            bgcolor: "#20304c",
            borderRadius: 3,
            boxShadow: 24,
            p: 4,
          }}
        >
          <Typography
            variant="h5"
            component="h2"
            gutterBottom
            sx={{ fontWeight: "bold", color: "#ebdeff" }}
          >
            📋 Clôture du Journal de Caisse
          </Typography>

          {activeJournal && (
            <Box
              sx={{
                mb: 3,
                p: 2,
                bgcolor: "rgba(235,222,255,0.06)",
                borderRadius: 1,
              }}
            >
              <Typography variant="body2" sx={{ color: "rgba(235,222,255,0.6)" }}>
                <strong>Journal:</strong> {activeJournal.code || "N/A"}
              </Typography>
              <Typography variant="body2" sx={{ color: "rgba(235,222,255,0.6)" }}>
                <strong>Date d'ouverture:</strong>{" "}
                {new Date(activeJournal.start_date).toLocaleDateString("fr-FR")}
              </Typography>
              <Typography variant="body2" sx={{ color: "rgba(235,222,255,0.6)" }}>
                <strong>Fond initial:</strong>{" "}
                {formatCurrency(activeJournal.fond_initial)} TND
              </Typography>
            </Box>
          )}

          <Typography variant="body1" sx={{ mb: 2, color: "rgba(235,222,255,0.5)" }}>
            Veuillez confirmer le fond final de caisse avant clôture:
          </Typography>

          <TextField
            fullWidth
            label="Fond Final (TND)"
            type="text"
            value={fondFinal}
            placeholder="0.000"
            inputProps={{
              readOnly: true,
              inputMode: "none",
              step: "0.001",
              min: "0",
            }}
            sx={{
              mb: 2,
              "& .MuiOutlinedInput-root": {
                color: "#ebdeff",
                "& fieldset": { borderColor: "rgba(235,222,255,0.15)" },
                "&:hover fieldset": { borderColor: "rgba(253,104,62,0.4)" },
                "&.Mui-focused fieldset": { borderColor: "#fd683e" },
              },
              "& .MuiInputLabel-root": { color: "rgba(235,222,255,0.45)" },
              "& .MuiInputLabel-root.Mui-focused": { color: "#fd683e" },
              "& input": { cursor: "pointer" },
            }}
            disabled={isProcessing}
          />

          <Box sx={{ mb: 2 }}>
            <KeyboardComponent
              context="numeric"
              onKeyPress={handleFundKeyPress}
              showLetters={false}
              setShowLetters={() => {}}
              disabled={isProcessing}
            />
          </Box>

          {parametrages && parametrages.defaultFondClotureInput ? (
            <Box
              sx={{
                mb: 3,
                p: 2,
                bgcolor: (() => {
                  const value = parseFloat(fondFinal) || 0;
                  const diff = value - currentFund;
                  if (diff > 0) return "rgba(46,125,50,0.18)";
                  if (diff < 0) return "rgba(211,47,47,0.18)";
                  return "rgba(235,222,255,0.06)";
                })(),
                borderRadius: 1,
                border: "1px solid rgba(235,222,255,0.15)",
              }}
            >
              <Typography variant="body2" sx={{ color: "#ebdeff" }}>
                <strong>Écart avec le fond courant:</strong>{" "}
                <span
                  style={{
                    color: (() => {
                      const value = parseFloat(fondFinal) || 0;
                      const diff = value - currentFund;
                      if (diff > 0) return "#66bb6a";
                      if (diff < 0) return "#ef5350";
                      return "rgba(235,222,255,0.7)";
                    })(),
                    fontWeight: "bold",
                  }}
                >
                  {formatCurrency((parseFloat(fondFinal) || 0) - currentFund)}{" "}
                  TND
                </span>
              </Typography>
              {currentFund > 0 && (
                <Typography
                  variant="caption"
                  sx={{ display: "block", mt: 0.5, color: "rgba(235,222,255,0.5)" }}
                >
                  {(parseFloat(fondFinal) || 0) > currentFund
                    ? "⚠️ Le fond final est supérieur au fond courant"
                    : (parseFloat(fondFinal) || 0) < currentFund
                      ? "⚠️ Le fond final est inférieur au fond courant"
                      : "✅ Montant correspond au fond courant"}
                </Typography>
              )}
            </Box>
          ) : null}

          {/* <Typography
            variant="body2"
            color="error"
            sx={{ mb: 3, fontStyle: "italic" }}
          >
            ⚠️ Attention: Cette action est irréversible. Après clôture, aucune
            transaction ne pourra être ajoutée à ce journal.
          </Typography> */}

          <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
            <Button
              onClick={handleModalClose}
              disabled={isProcessing}
              sx={{ color: "rgba(235,222,255,0.5)" }}
            >
              Annuler
            </Button>
            <Button
              onClick={handleCloseJournal}
              variant="contained"
              color="primary"
              disabled={isProcessing || !fondFinal}
              sx={{
                backgroundColor: "#d32f2f",
                "&:hover": { backgroundColor: "#b71c1c" },
              }}
            >
              {isProcessing ? "Traitement..." : "Confirmer la clôture"}
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Transfer Stock Modal */}
      <Modal
        open={showTransferStockModal}
        onClose={handleModalClose}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            width: 800,
            maxWidth: "90vw",
            maxHeight: "90vh",
            overflow: "auto",
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            p: 4,
          }}
        >
          <Typography
            variant="h5"
            component="h2"
            gutterBottom
            sx={{ fontWeight: "bold", color: "#2c3e50", mb: 3 }}
          >
            🔄 Transfert de Stock entre Caisses
          </Typography>

          {activeJournal && (
            <Alert severity="info" sx={{ mb: 3 }}>
              <strong>Caisse source:</strong>{" "}
              {activeJournal.caisse_nom || "Caisse actuelle"} |
              <strong> Disponible:</strong>{" "}
              {formatCurrency(activeJournal.fond_courant || 0)} TND
            </Alert>
          )}

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 3,
              mb: 4,
            }}
          >
            {/* From Caisse - Readonly */}
            <FormControl fullWidth>
              <InputLabel>Caisse source</InputLabel>
              <Select value={storedCaisseID} label="Caisse source" disabled>
                <MenuItem value={storedCaisseID}>{storedCaisse?.nom}</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel id="to-caisse-label">Caisse destination *</InputLabel>
              <Select
                labelId="to-caisse-label"
                value={selectedToCaisse || ""}
                onChange={(e) => setSelectedToCaisse(e.target.value)}
                label="Caisse destination *"
                disabled={loadingCaisses || isProcessing}
                MenuProps={selectMenuProps} // Applying the fix
              >
                {caisses.length > 0 ? (
                  caisses.map((caisse) => (
                    <MenuItem key={caisse.id} value={caisse.id}>
                      {caisse.nom}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled>Aucune autre caisse disponible</MenuItem>
                )}
              </Select>
            </FormControl>

            {/* Product Select */}
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel id="product-label">Produit *</InputLabel>
              <Select
                labelId="product-label"
                value={selectedProduct || ""}
                onChange={(e) => setSelectedProduct(e.target.value)}
                label="Produit *"
                disabled={loadingProducts || isProcessing}
                MenuProps={selectMenuProps} // Applying the fix
              >
                {products
                  .filter((product) =>
                    product.all_stocks?.some(
                      (stock) => stock.caisse_id === storedCaisseID,
                    ),
                  )
                  .map((product) => {
                    const matchingStock = product.all_stocks.find(
                      (stock) => stock.caisse_id === storedCaisseID,
                    );

                    return (
                      <MenuItem key={product._id} value={product._id}>
                        {product.titre?.fr || product.titre}
                        {` (Stock: ${matchingStock.quantite_stock})`}
                      </MenuItem>
                    );
                  })}
              </Select>
            </FormControl>

            {/* Quantity */}
            <TextField
              fullWidth
              label="Quantité *"
              type="number"
              value={transferQuantity}
              onChange={(e) => setTransferQuantity(e.target.value)}
              placeholder="0"
              inputProps={{
                min: 1,
                max: availableStock,
              }}
              disabled={isProcessing || !selectedProduct}
              helperText={
                availableStock > 0 ? `Disponible: ${availableStock}` : ""
              }
            />
          </Box>

          {/* Transfer Button */}
          <Box sx={{ display: "flex", justifyContent: "center", mb: 4 }}>
            <Button
              variant="contained"
              size="large"
              onClick={handleTransferStock}
              disabled={
                isProcessing ||
                !selectedToCaisse ||
                !selectedProduct ||
                !transferQuantity
              }
              sx={{
                minWidth: 200,
                backgroundColor: "#1976d2",
                "&:hover": { backgroundColor: "#1565c0" },
              }}
            >
              {isProcessing
                ? "Transfert en cours..."
                : "Effectuer le transfert"}
            </Button>
          </Box>

          {/* Transfer History */}
        </Box>
      </Modal>

      {/* Menu Bar */}
      <div
        style={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          width: "100%",
          backgroundColor: "#b7d9f7",
          padding: "12px 20px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderTop: "1px solid #e2e2ee",
          boxShadow: "0 -3px 10px rgba(0,0,0,0.08)",
          zIndex: 1000,
          transition: "height 0.3s ease",
          overflow: "hidden",
        }}
      >
        <div
          ref={leftMenuRef}
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
            gap: "15px",
            flex: 1,
            maxWidth: "calc(100% - 250px)",
            overflow: "hidden",
            maxHeight: menuExpanded ? "140px" : "60px",
            transition: "max-height 0.3s ease",
          }}
        >
          <button
            onClick={onOrdersClick}
            style={{
              minWidth: "140px",
              height: "50px",
              fontSize: "16px",
              fontWeight: "600",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              borderRadius: "12px",
              background: "#b7d9f7",
              color: "#2c3e50",
              border: "1px solid #2c3e50",
              cursor: "pointer",
              transition: "all 0.3s ease",
              flexShrink: 0,
            }}
            onMouseOver={(e) => (e.target.style.background = "#c8e3ff")}
            onMouseOut={(e) => (e.target.style.background = "#b7d9f7")}
          >
            <i className="fas fa-shopping-basket"></i> Commandes
          </button>

          <button
            onClick={handleOpenCloseJournal}
            style={{
              minWidth: "140px",
              height: "50px",
              fontSize: "16px",
              fontWeight: "600",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              borderRadius: "12px",
              background: activeJournal?.cloture ? "#cccccc" : "#b7d9f7",
              color: activeJournal?.cloture ? "#666666" : "#2c3e50",
              border: activeJournal?.cloture
                ? "1px solid #999999"
                : "1px solid #2c3e50",
              cursor: activeJournal?.cloture ? "not-allowed" : "pointer",
              transition: "all 0.3s ease",
              flexShrink: 0,
            }}
            onMouseOver={(e) => {
              if (!activeJournal?.cloture) {
                e.target.style.background = "#c8e3ff";
              }
            }}
            onMouseOut={(e) => {
              if (!activeJournal?.cloture) {
                e.target.style.background = "#b7d9f7";
              }
            }}
            disabled={activeJournal?.cloture}
            title={
              activeJournal?.cloture
                ? "Journal déjà clôturé"
                : "Clôturer le journal de caisse"
            }
          >
            <i className="fas fa-cash-register"></i>
            {activeJournal?.cloture ? "Journal clos" : "Clôturer caisse"}
          </button>

          {organisations[0].type === "Restaurant" && (
            <button
              onClick={onTablesClick}
              style={{
                minWidth: "140px",
                height: "50px",
                fontSize: "16px",
                fontWeight: "600",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
                borderRadius: "12px",
                background: "#b7d9f7",
                color: "#2c3e50",
                border: "1px solid #2c3e50",
                cursor: "pointer",
                transition: "all 0.3s ease",
                flexShrink: 0,
              }}
              onMouseOver={(e) => (e.target.style.background = "#c8e3ff")}
              onMouseOut={(e) => (e.target.style.background = "#b7d9f7")}
            >
              <i className="fas fa-utensils"></i>
              Tables
            </button>
          )}

          <button
            onClick={() => connectPrinter()}
            style={{
              minWidth: "140px",
              height: "50px",
              fontSize: "16px",
              fontWeight: "600",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              borderRadius: "12px",
              background: "#b7d9f7",
              color: "#2c3e50",
              border: "1px solid #2c3e50",
              cursor: "pointer",
              transition: "all 0.3s ease",
              flexShrink: 0,
            }}
            onMouseOver={(e) => (e.target.style.background = "#c8e3ff")}
            onMouseOut={(e) => (e.target.style.background = "#b7d9f7")}
          >
            <i className="fas fa-print"></i> Imprimante
          </button>

          <button
            onClick={() => handleOpenDrawer()}
            style={{
              minWidth: "140px",
              height: "50px",
              fontSize: "16px",
              fontWeight: "600",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              borderRadius: "12px",
              background: "#b7d9f7",
              color: "#2c3e50",
              border: "1px solid #2c3e50",
              cursor: "pointer",
              transition: "all 0.3s ease",
              flexShrink: 0,
            }}
            onMouseOver={(e) => (e.target.style.background = "#c8e3ff")}
            onMouseOut={(e) => (e.target.style.background = "#b7d9f7")}
          >
            <i className="fas fa-window-maximize"></i> Tiroir
          </button>
          <button
            onClick={handleOpenTransferStock}
            style={{
              minWidth: "140px",
              height: "50px",
              fontSize: "16px",
              fontWeight: "600",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              borderRadius: "12px",
              background: "#b7d9f7",
              color: "#2c3e50",
              border: "1px solid #2c3e50",
              cursor: activeJournal?.cloture ? "not-allowed" : "pointer",
              transition: "all 0.3s ease",
              flexShrink: 0,
              opacity: activeJournal?.cloture ? 0.5 : 1,
            }}
            onMouseOver={(e) => {
              if (!activeJournal?.cloture) {
                e.target.style.background = "#c8e3ff";
              }
            }}
            onMouseOut={(e) => {
              if (!activeJournal?.cloture) {
                e.target.style.background = "#b7d9f7";
              }
            }}
            disabled={activeJournal?.cloture}
            title={
              activeJournal?.cloture
                ? "Journal clôturé"
                : "Transférer du stock à une autre caisse"
            }
          >
            <i className="fas fa-exchange-alt"></i>
            Transfert Stock
          </button>
        </div>

        <div
          style={{
            width: "1px",
            height: "50px",
            backgroundColor: "#2c3e507a",
            margin: "0 20px",
            borderRadius: "2px",
            flexShrink: 0,
            alignSelf: "center",
          }}
        ></div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "15px",
            flexShrink: 0,
            minWidth: "200px",
            justifyContent: "flex-end",
          }}
        >
          <button
            onClick={toggleMenuExpansion}
            style={{
              width: "40px",
              height: "40px",
              borderRadius: "50%",
              background: "#2c3e50",
              color: "white",
              border: "none",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginLeft: "10px",
              flexShrink: 0,
            }}
            title={menuExpanded ? "Réduire le menu" : "Étendre le menu"}
          >
            <i className={`fas fa-chevron-${menuExpanded ? "down" : "up"}`}></i>
          </button>
          {isAdmin && (
            <button
              onClick={() => navigate("/")}
              style={{
                minWidth: "130px",
                height: "50px",
                fontSize: "16px",
                fontWeight: "600",
                borderRadius: "12px",
                background: "#b7d9f7",
                color: "#2c3e50",
                border: "1px solid #2c3e50",
                cursor: "pointer",
                transition: "all 0.3s ease",
                flexShrink: 0,
              }}
              onMouseOver={(e) => (e.target.style.background = "#c8e3ff")}
              onMouseOut={(e) => (e.target.style.background = "#b7d9f7")}
              title="Retour au tableau de bord admin"
            >
              <i className="fas fa-home"></i>
              Dashboard
            </button>
          )}
          <button
            onClick={onLogoutClick}
            style={{
              minWidth: "130px",
              height: "50px",
              fontSize: "16px",
              fontWeight: "600",
              borderRadius: "12px",
              background: "#f7b7c3",
              color: "#5a2b33",
              border: "1px solid #5a2b33",
              cursor: "pointer",
              transition: "all 0.3s ease",
              flexShrink: 0,
            }}
            onMouseOver={(e) => (e.target.style.background = "#ffc7d2")}
            onMouseOut={(e) => (e.target.style.background = "#f7b7c3")}
          >
            <i className="fa fa-sign-out"></i>
            Déconnexion
          </button>
        </div>
      </div>
    </>
  );
};

export default GlobalMenu;
